import { z } from "zod";

import { COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS, COGNITO_INTERNAL_STAFF_GROUPS } from "@recobra/core";

import { CaseType, EXTERNAL_CODE_MAX, PrescriptionPeriodYearsSchema } from "../case-core";
import { cnpjSchema, cpfSchema } from "../client";
import { brazilPhoneSchema } from "../client";
import { NoteSchema } from "../note";
import type { VisibilityRule } from "../utils";
import { isValidCnh, isValidLicensePlate, isValidRg } from "./field-validation";

export const IncidentSchema = z.object({
  date: z.iso
    .date({ message: "case_validation_incident_date_required" })
    .describe("Incident date in YYYY-MM-DD format")
    .meta({ visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS }),
  time: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional()
    .describe("Incident time in 24-hour HH:mm format, when known")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  location: z
    .string({ message: "case_validation_location_required" })
    .min(1, { message: "case_validation_location_required" })
    .max(200)
    .describe("Where the incident occurred")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  description: z
    .string({ message: "case_validation_description_required" })
    .min(1, { message: "case_validation_description_required" })
    .max(2000)
    .describe("Narrative description of the incident")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
});

export const DraftIncidentSchema = IncidentSchema.partial();
export type DraftIncident = z.infer<typeof DraftIncidentSchema>;

const LicensePlateSchema = z
  .string({ message: "case_validation_license_plate_required" })
  .min(1, { message: "case_validation_license_plate_required" })
  .refine(isValidLicensePlate, {
    message: "case_validation_license_plate_invalid",
  });

const OptionalLicensePlateSchema = z
  .string()
  .optional()
  .refine((v) => !v || isValidLicensePlate(v), {
    message: "case_validation_license_plate_invalid",
  });

const CnhSchema = z.string().refine(isValidCnh, {
  message: "case_validation_cnh_invalid",
});

const RgSchema = z.string().refine(isValidRg, {
  message: "case_validation_rg_invalid",
});

export const VehicleDetailsSchema = z.object({
  licensePlate: OptionalLicensePlateSchema,
  make: z.string().max(50).optional(),
  model: z.string().max(50).optional(),
  color: z.string().max(30).optional(),
  owner: z.string().max(120).optional(),
  year: z
    .number()
    .int()
    .min(1900)
    .max(new Date().getFullYear() + 1)
    .optional(),
});
export type VehicleDetails = z.infer<typeof VehicleDetailsSchema>;

export const RequiredVehicleDetailsSchema = VehicleDetailsSchema.extend({
  licensePlate: LicensePlateSchema,
});

export const LiableVehicleSchema = VehicleDetailsSchema.extend({
  isCompany: z
    .boolean()
    .optional()
    .describe("Whether the debtor is a company rather than an individual"),
  companyName: z.string().max(200).optional().describe("Company name when isCompany is true"),
});

export type LiableVehicle = z.infer<typeof LiableVehicleSchema>;

/**
 * The liable vehicle worth showing — the first one carrying any identifying
 * detail — alongside how many further liable vehicles the case holds.
 */
export const primaryLiableVehicle = <
  T extends Pick<VehicleDetails, "licensePlate" | "make" | "model">,
>(
  vehicles: readonly T[] | null | undefined,
): { vehicle: T | undefined; additionalCount: number } => ({
  vehicle: vehicles?.find(({ licensePlate, make, model }) => licensePlate || make || model),
  additionalCount: Math.max((vehicles?.length ?? 0) - 1, 0),
});

const RequiredLiableVehicleSchema = LiableVehicleSchema.extend({
  licensePlate: LicensePlateSchema,
});

export const RequiredLiableVehiclesSchema = z
  .array(RequiredLiableVehicleSchema)
  .min(1, { message: "case_validation_liable_vehicle_required" });

export const CaseContactType = z.enum([
  "claimant_driver",
  "liable_driver",
  "insurer",
  "current_owner",
  "owner_at_loss",
  "insured",
  "debtor",
  "other",
]);
export type CaseContactType = z.infer<typeof CaseContactType>;

export const CaseSectionKey = z.enum([
  "incident",
  "claimantVehicle",
  "claimantDriver",
  "liableVehicle",
  "liableDriver",
  "damages",
  "debtor",
  "debt",
]);
export type CaseSectionKey = z.infer<typeof CaseSectionKey>;

export const CaseSectionReviewSchema = z.object({
  checked: z.boolean(),
  checkedBy: z.string().min(1),
  checkedAt: z.iso.datetime(),
  mutationId: z.uuid(),
});
export type CaseSectionReview = z.infer<typeof CaseSectionReviewSchema>;

/**
 * Contact roles a case form always renders a field for, in slot order —
 * the single source of truth both `seedContactsForCaseType` (frontend
 * rendering) and `requiredCaseContactsSchemaForType` (required-case-contacts.ts) build on, so a
 * case type's fixed contact slots can't drift between what the form shows
 * and what validation requires.
 */
export const FIXED_CASE_CONTACT_ROLES: Partial<Record<CaseType, readonly CaseContactType[]>> = {
  third_party_claim: ["claimant_driver", "liable_driver"],
  road_conditions: ["claimant_driver"],
  debt_collection: ["debtor"],
};

const CaseContactEntityType = z.enum(["person", "company"]);
export type CaseContactEntityType = z.infer<typeof CaseContactEntityType>;

const contactIdentityFields = {
  cnh: CnhSchema.optional()
    .describe("Brazilian driver's license (CNH) number")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  rg: RgSchema.optional()
    .describe("Brazilian identity document (RG) number")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  cpf: cpfSchema
    .optional()
    .describe("Brazilian tax ID (CPF)")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  address: z.string().max(300).optional().meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
};

const CaseConversationImportSchema = z.object({
  id: z.uuid(),
  rawText: z
    .string()
    .min(1)
    .max(50000)
    .describe("A manually submitted transcript batch, not a provider message"),
  importedAt: z.iso.datetime().describe("When the transcript was imported into Recobra"),
});
export type CaseConversationImport = z.infer<typeof CaseConversationImportSchema>;

const CaseContactObjectSchema = z.object({
  id: z.uuid().meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  type: CaseContactType.meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  entityType: CaseContactEntityType.optional()
    .describe("Whether this contact is an individual or a company; absent legacy values are people")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  name: z
    .string()
    .max(200, { message: "case_validation_contact_name_too_long" })
    .optional()
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  phone1: brazilPhoneSchema.meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  phone2: brazilPhoneSchema.meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  email: z
    .email("case_validation_email_invalid")
    .max(254, "case_validation_email_invalid")
    .optional()
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  observation: z.string().max(500).optional().meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  conversationImports: z
    .array(CaseConversationImportSchema)
    .optional()
    .describe("Manual transcript imports; provider-backed messages are stored separately")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  ...contactIdentityFields,
  cnpj: cnpjSchema
    .optional()
    .describe("Brazilian company tax ID (CNPJ)")
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
});

const rejectConflictingContactIdentifiers = (
  contact: {
    entityType?: CaseContactEntityType;
    cpf?: string;
    rg?: string;
    cnh?: string;
    cnpj?: string;
  },
  context: z.RefinementCtx,
): void => {
  if (contact.entityType === "company") {
    for (const field of ["cpf", "rg", "cnh"] as const) {
      if (contact[field]) {
        context.addIssue({
          code: "custom",
          message: "case_validation_company_person_identifier_conflict",
          path: [field],
        });
      }
    }
  } else if (contact.cnpj) {
    context.addIssue({
      code: "custom",
      message: "case_validation_person_company_identifier_conflict",
      path: ["cnpj"],
    });
  }
};

export const CaseContactSchema = CaseContactObjectSchema.superRefine(
  rejectConflictingContactIdentifiers,
).describe("A person or organization connected to the case");
export type CaseContact = z.infer<typeof CaseContactSchema>;

export const AnalysedCaseContactSchema = CaseContactObjectSchema.omit({
  id: true,
  conversationImports: true,
}).superRefine(rejectConflictingContactIdentifiers);

export const RequiredCaseContactSchema = CaseContactSchema.safeExtend({
  name: z
    .string({ message: "case_validation_contact_name_required" })
    .max(200, { message: "case_validation_contact_name_too_long" })
    .trim()
    .min(1, { message: "case_validation_contact_name_required" }),
}).describe("A contact that can be saved on its own — a name is required");

export const DeletedCaseContactSchema = CaseContactSchema.safeExtend({
  deletedAt: z.iso.datetime(),
});
export type DeletedCaseContact = z.infer<typeof DeletedCaseContactSchema>;

export const rejectDuplicateContactIds = (
  contacts: CaseContact[],
  context: z.RefinementCtx,
): void => {
  const ids = new Set<string>();

  contacts.forEach((contact, index) => {
    if (ids.has(contact.id)) {
      context.addIssue({
        code: "custom",
        message: "case_validation_contact_id_duplicate",
        path: [index, "id"],
      });
    }
    ids.add(contact.id);
  });
};

export const CaseContactInputSchema = CaseContactObjectSchema.omit({
  conversationImports: true,
}).superRefine(rejectConflictingContactIdentifiers);

export const CaseContactsSchema = z
  .array(CaseContactSchema)
  .superRefine(rejectDuplicateContactIds)
  .default([]);

export const CaseContactsInputSchema = z
  .array(CaseContactInputSchema)
  .superRefine(rejectDuplicateContactIds)
  .describe("Editable case contacts without server-owned conversation imports");

export const firstContactOfType = (
  contacts: readonly CaseContact[] | null | undefined,
  type: CaseContactType,
): CaseContact | undefined => contacts?.find((contact) => contact.type === type);

const contactResponseOfType = (type: CaseContactType, visibleTo: VisibilityRule) =>
  CaseContactSchema.safeExtend({
    id: CaseContactSchema.shape.id.meta({ visibleTo }),
    type: z.literal(type).meta({ visibleTo }),
    name: CaseContactSchema.shape.name.meta({ visibleTo }),
  }).meta({ visibleTo });

export const CaseContactResponseSchema = z.discriminatedUnion("type", [
  contactResponseOfType("claimant_driver", COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS),
  contactResponseOfType("liable_driver", COGNITO_INTERNAL_STAFF_GROUPS),
  contactResponseOfType("insurer", COGNITO_INTERNAL_STAFF_GROUPS),
  contactResponseOfType("current_owner", COGNITO_INTERNAL_STAFF_GROUPS),
  contactResponseOfType("owner_at_loss", COGNITO_INTERNAL_STAFF_GROUPS),
  contactResponseOfType("insured", COGNITO_INTERNAL_STAFF_GROUPS),
  contactResponseOfType("debtor", COGNITO_INTERNAL_STAFF_GROUPS),
  contactResponseOfType("other", COGNITO_INTERNAL_STAFF_GROUPS),
]);

export const debtTypes = [
  "contractual_installment",
  "contractual_penalty",
  "return_inspection_damage",
  "traffic_fine",
  "other",
] as const;

const DebtType = z.enum(debtTypes, {
  message: "case_validation_debt_type_required",
});
type DebtType = z.infer<typeof DebtType>;

export const DebtEntrySchema = z.object({
  id: z.string().describe("Unique identifier for form tracking"),
  type: DebtType,
  description: z.string().max(2000).optional().describe("Free-form comments about this debt entry"),
  dueDate: z.iso
    .date({ message: "case_validation_debt_due_date_invalid" })
    .optional()
    .describe("Due date printed on the invoice or bank slip, when there is one"),
  amount: z
    .number({ message: "case_validation_debt_amount_required" })
    .positive({ message: "case_validation_debt_amount_positive" }),
  sourceDocumentId: z
    .uuid()
    .optional()
    .describe(
      "The upload this value was extracted from by AI; unset for manually entered/edited entries",
    )
    .meta({ visibleTo: "*" }),
});

const DraftDebtEntrySchema = DebtEntrySchema.partial({ type: true, amount: true });
export type DraftDebtEntry = z.infer<typeof DraftDebtEntrySchema>;

export const DebtSchema = z.object({
  prescriptionPeriodYears: PrescriptionPeriodYearsSchema.optional().describe(
    "Statute-of-limitations period applied to every entry's due date; each entry's deadline is projected by debtEntryPrescriptionDate, never stored",
  ),
  entries: z
    .array(DebtEntrySchema)
    .min(1, { message: "case_validation_debt_entries_required" })
    .describe("Debt line items; the case owes their sum"),
});
export type Debt = z.infer<typeof DebtSchema>;
export const DraftDebtSchema = DebtSchema.extend({
  entries: z.array(DraftDebtEntrySchema),
}).partial();
export type DraftDebt = z.infer<typeof DraftDebtSchema>;

export const damageTypes = [
  "material_damage",
  "lost_income",
  "medical_hospital_expenses",
  "franchise_reimbursement",
  "other",
] as const;

const DamageType = z.enum(damageTypes, {
  message: "case_validation_damage_type_required",
});
export type DamageType = z.infer<typeof DamageType>;

export const DamageEntrySchema = z.object({
  id: z.string().describe("Unique identifier for form tracking"),
  type: DamageType,
  description: z
    .string()
    .max(2000)
    .optional()
    .describe("Free-form comments about this damage entry and any value calculation"),
  amount: z
    .number({ message: "case_validation_damage_amount_required" })
    .nonnegative({ message: "case_validation_damage_amount_nonnegative" }),
  sourceDocumentId: z
    .uuid()
    .optional()
    .describe(
      "The upload this value was extracted from by AI; unset for manually entered/edited entries",
    )
    .meta({ visibleTo: "*" }),
});

export const DraftDamageEntrySchema = DamageEntrySchema.partial({ type: true, amount: true });
export type DraftDamageEntry = z.infer<typeof DraftDamageEntrySchema>;

export const CaseBaseSchema = z.object({
  id: z.uuid().optional().describe("Client-generated case ID; used for pre-upload before creation"),
  clientId: z.uuid({ message: "case_validation_client_required" }),
  branchId: z
    .uuid()
    .optional()
    .describe(
      "Branch of the client, when the client has branches; required at creation when the client has active branches (see resolveCaseBranchSelection)",
    ),
  caseType: CaseType,
  externalCode: z
    .string()
    .max(EXTERNAL_CODE_MAX)
    .optional()
    .describe("Client-provided reference code for the case"),
  incident: IncidentSchema.optional().describe(
    "Required for third_party_claim and road_conditions",
  ),
  debt: DebtSchema.optional().describe("Debt collection line items and prescription period"),
  prescriptionPeriodYears: PrescriptionPeriodYearsSchema.optional()
    .describe(
      "Statute-of-limitations period in years; defaults to PRESCRIPTION_PERIOD.defaultYears when unset. The deadline itself is never stored — see casePrescriptionDate, which projects it from this and the incident date.",
    )
    .meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  liableVehicles: z
    .array(LiableVehicleSchema)
    .optional()
    .describe("Liable vehicles (third_party_claim)"),
  vehicle: VehicleDetailsSchema.optional().describe("Vehicle involved in the incident"),
  contacts: CaseContactsSchema,
  damages: z.array(DamageEntrySchema).describe("Financial damage line items"),
  notes: z.array(NoteSchema).default([]).describe("Free-form case notes"),
});
